import { Component, Input, OnChanges, SimpleChanges } from "@angular/core";
import { cloneDeep } from "lodash";
import { finalize } from "rxjs/operators";
import { environment as appEnv } from "../../../environments/environment";
import { CommitsUrlService } from "../../commits-url/commits-url.service";
import { DiffStatHotspotsService } from "../../models/diff-stat-hotspots.service";
import { TimeDurAgoService } from "../../time-dur-ago/time-dur-ago.service";
import { pluralize } from "../../utils/pluralize";
import { HotspotDotColorService } from "./dot-colors.service";
import { HotspotIdentifiers } from "./hotspot-identifiers";

@Component({
  selector: "ab-diff-stat-hotspots",
  templateUrl: "./diff-stat-hotspots.component.html",
})
export class DiffStatHotspotsComponent implements OnChanges {
  @Input() public featureDisabled: boolean;
  @Input() public identifier: string;
  @Input() public project: any;
  @Input() public dur: string;
  @Input() public lastN: number;

  public appEnv = appEnv;
  public diffStatIntegrationConfigured: boolean;
  public loading: boolean;
  public showEmptyState: boolean;
  public showingSampleHotspots: boolean;
  public hotspots: any;

  private sampleHotspots = {
    most_changed_files: {
      "folder/subfolder/file.rb": { additions: 15240, deletions: 7898 },
      "foldername/subfoldername/filename.rb": {
        additions: 13570,
        deletions: 8204,
      },
      "folder2/subfolder2/file2.rb": { additions: 10422, deletions: 7284 },
      "foldername2/subfoldername2/filename2.rb": {
        additions: 8452,
        deletions: 5545,
      },
      "folder3/subfolder3/file3.rb": { additions: 7209, deletions: 6807 },
      "folder4/subfolder1/file.js": { additions: 6100, deletions: 5200 },
      "folder5/subfoldername4/file2.rb": { additions: 5807, deletions: 4710 },
      "folder6/subfolder2/filename3.js": { additions: 2103, deletions: 1204 },
      "folder7/subfoldername3/file4.ts": { additions: 110, deletions: 100 },
      "folder8/subfolder1/filename7.rb": { additions: 90, deletions: 72 },
    },
    most_touched_files: {
      "folder/subfolder1/file.rb": { touched: 18 },
      "folder/subfolder2/file.rb": { touched: 17 },
      "folder2/subfolder2/file2.rb": { touched: 16 },
      "foldername2/subfoldername2/filename2.rb": { touched: 13 },
      "folder3/subfolder3/file3.rb": { touched: 12 },
      "folder4/subfolder1/file.js": { touched: 11 },
      "folder5/subfoldername4/file2.rb": { touched: 10 },
      "folder6/subfolder2/filename3.js": { touched: 4 },
      "folder7/subfoldername3/file4.ts": { touched: 2 },
      "folder8/subfolder1/filename7.rb": { touched: 1 },
    },
  };

  constructor(
    private commitsUrlService: CommitsUrlService,
    private diffStatHotspotsService: DiffStatHotspotsService,
    private hotspotDotColorService: HotspotDotColorService,
    private timeDurAgoService: TimeDurAgoService,
  ) {}

  public ngOnChanges(changes: SimpleChanges) {
    if (this.featureDisabled) {
      return;
    }
    if (!changes.identifier && !changes.dur && !changes.lastN) {
      return;
    }

    let collectingDiffStats = false;
    if (this.project.integrations) {
      const github = this.project.integrations.github;
      const gitlab = this.project.integrations.gitlab;
      const bitbucket = this.project.integrations.bitbucket;

      this.diffStatIntegrationConfigured = !!(github || gitlab || bitbucket);

      collectingDiffStats =
        (github && github.collectDiffStats) ||
        (gitlab && gitlab.collectDiffStats) ||
        (bitbucket && bitbucket.collectDiffStats);
    }

    const params = {
      projectId: this.project.id,
      createdAfter: this.timeDurAgoService.timeFor(this.dur, this.lastN),
    };

    this.loading = true;
    this.showEmptyState = false;
    this.showingSampleHotspots = false;

    this.diffStatHotspotsService
      .get(params)
      .pipe(
        finalize(() => {
          this.loading = false;
        }),
      )
      .subscribe((hotspots: any) => {
        const emptyHotspots =
          Object.keys(hotspots[this.identifier]).length === 0;

        if (emptyHotspots && !collectingDiffStats) {
          hotspots = cloneDeep(this.sampleHotspots);
          this.showingSampleHotspots = true;
        } else if (emptyHotspots) {
          this.showEmptyState = true;
        }

        hotspots = this.setDotColors(hotspots[this.identifier]);

        if (!this.showingSampleHotspots) {
          this.hotspots = this.setCommitUrls(hotspots);
        } else {
          this.hotspots = hotspots;
        }
      });
  }

  public trackBy(_index: number, hotspot: any) {
    return hotspot.key;
  }

  // Do not sort the object by defaultComparator and display it as is since we
  // receive it sorted from the backend. For more info about defaultComparator
  // please visit https://angular.io/api/common/KeyValuePipe.
  public orderBy(_a: any, _b: any) {
    return 0;
  }

  public diffStatHotspotTooltip(stats: any, file: string) {
    if (this.showChangedFileStats()) {
      return (
        `There were ${stats.additions} code ` +
        `${pluralize(stats.additions, "addition")} and ` +
        `${stats.deletions} code ` +
        `${pluralize(stats.deletions, "deletion")} in ${file}`
      );
    } else {
      return (
        `${file} was modified in ${stats.touched} ` +
        `${pluralize(stats.touched, "commit")}`
      );
    }
  }

  public showChangedFileStats() {
    return this.identifier === HotspotIdentifiers.DIFF_STATS.changed;
  }

  public showTouchedFileStats() {
    return this.identifier === HotspotIdentifiers.DIFF_STATS.touched;
  }

  private setCommitUrls(hotspots: any) {
    for (const file in hotspots) {
      if (hotspots[file]) {
        hotspots[file].commitsUrl = this.commitsUrlService.urlFor(
          this.project,
          file,
        );
      }
    }

    return hotspots;
  }

  private setDotColors(hotspots: any) {
    const hotspotsLength = Object.keys(hotspots).length;
    const hotspotsSum = this.sumHotspotDiffStats(hotspots);

    for (const file in hotspots) {
      if (hotspots[file]) {
        hotspots[file].dotColor = this.hotspotDotColorService.getDotColor(
          hotspots[file].sum,
          hotspotsSum,
          hotspotsLength,
        );
      }
    }

    return hotspots;
  }

  private sumHotspotDiffStats(hotspots: any) {
    let hotspotDiffStatsSum = 0;

    for (const file in hotspots) {
      if (hotspots[file]) {
        const keys = Object.keys(hotspots[file]);
        hotspots[file].sum = 0;

        for (const key of keys) {
          hotspots[file].sum += hotspots[file][key];
        }

        hotspotDiffStatsSum += hotspots[file].sum;
      }
    }

    return hotspotDiffStatsSum;
  }
}
